import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Spinner } from 'reactstrap';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import TitledContainer from '../../../components/TitledContainer';
import { CampaignAnalytics, CampaignProcessStatus } from '../../../models';
import { getCampaignAnalyticsAsync, sendCampaignAsync } from '../../../ajax/requests';
import { showError } from '../../../store/entities/actions';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { paths } from '../../../app/constants';
import { CampaignAnalyticsHeader } from './CampaignAnalyticsHeader';
import { CampaignMetricStrip } from './CampaignMetricStrip';
import { CampaignProcessTimeline } from './CampaignProcessTimeline';
import { CampaignRecipientStatusTable } from './CampaignRecipientStatusTable';
import { CampaignFailureBreakdown } from './CampaignFailureBreakdown';
import { CampaignReTriggerReviewModal } from './CampaignReTriggerReviewModal';
import { formatCampaignDateTime } from '../campaignsUtils';

const PageBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-M);
`;

const LoadingState = styled.div`
    display: flex;
    justify-content: center;
    padding: var(--spacer-L);
`;

const bannerBase = `
    padding: var(--spacer-S);
    border-radius: var(--spacer-XS);
    font-weight: 600;
    font-size: 0.9rem;
`;

const ReTriggerBannerEligible = styled.div`
    ${bannerBase}
    border: var(--border-thin) solid var(--success-border);
    background: var(--success-bg);
    color: var(--success-color);
`;

const ReTriggerBannerCooldown = styled.div`
    ${bannerBase}
    border: var(--border-thin) solid var(--warning-border);
    background: var(--warning-bg);
    color: var(--text-body);
`;

const TwoColumnGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacer-M);

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

interface AnalyticsParams {
    id: string;
}

const isSendInProgress = (analytics?: CampaignAnalytics): boolean =>
    analytics?.processes.some(process =>
        process.status === CampaignProcessStatus.Created || process.status === CampaignProcessStatus.Sending
    ) ?? false;

const getReTriggerBannerText = (analytics: CampaignAnalytics | undefined, t: (key: TK, ...args: string[]) => string): string => {
    if (!analytics) return '';
    if (isSendInProgress(analytics)) {
        return t(TK.campaignAnalyticsSendInProgress);
    }
    if (analytics.isReTriggerEligible) {
        return t(TK.campaignAnalyticsReTriggerEligible);
    }
    if (analytics.reTriggerEligibleAt) {
        return `${t(TK.campaignAnalyticsReTriggerNotEligible)} - ${t(TK.campaignAnalyticsEligibleFrom)} ${formatCampaignDateTime(analytics.reTriggerEligibleAt)}`;
    }
    return t(TK.campaignAnalyticsReTriggerNotEligible);
};

export const CampaignAnalyticsPage: React.FC = () => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const history = useHistory();
    const { id } = useParams<AnalyticsParams>();

    const [loading, setLoading] = useState(true);
    const [refreshLoading, setRefreshLoading] = useState(false);
    const [reTriggerLoading, setReTriggerLoading] = useState(false);
    const [reTriggerReviewOpen, setReTriggerReviewOpen] = useState(false);
    const [analytics, setAnalytics] = useState<CampaignAnalytics | undefined>();
    const isMountedRef = useRef(true);

    const backToBuilder = useCallback(() => {
        history.push(paths.campaign(id));
    }, [history, id]);

    const loadAnalytics = useCallback(async () => {
        setLoading(true);

        try {
            const data = await getCampaignAnalyticsAsync(id);
            if (!isMountedRef.current) {
                return;
            }
            if (!data.processes.length) {
                history.replace(paths.campaign(id));
                return;
            }
            setAnalytics(data);
        } catch (error) {
            if (!isMountedRef.current) {
                return;
            }
            console.error(error);
            dispatch(showError(error));
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [dispatch, history, id]);

    useEffect(() => {
        isMountedRef.current = true;
        loadAnalytics();

        return () => {
            isMountedRef.current = false;
        };
    }, [loadAnalytics]);

    const handleRefresh = useCallback(async () => {
        setRefreshLoading(true);
        try {
            const data = await getCampaignAnalyticsAsync(id);
            if (isMountedRef.current) {
                setAnalytics(data);
            }
        } catch (error) {
            if (isMountedRef.current) {
                console.error(error);
                dispatch(showError(error));
            }
        } finally {
            if (isMountedRef.current) {
                setRefreshLoading(false);
            }
        }
    }, [dispatch, id]);

    const canReTrigger = useMemo(() => !!analytics && analytics.isReTriggerEligible && !isSendInProgress(analytics), [analytics]);

    const reTriggerLabel = analytics ? getReTriggerBannerText(analytics, t) : '';

    const openReTriggerReview = useCallback(() => {
        setReTriggerReviewOpen(true);
    }, []);

    const closeReTriggerReview = useCallback(() => {
        if (reTriggerLoading) return;
        setReTriggerReviewOpen(false);
    }, [reTriggerLoading]);

    const confirmReTrigger = useCallback(async () => {
        if (!analytics || !canReTrigger) return;

        setReTriggerLoading(true);
        try {
            await sendCampaignAsync(id);
            if (isMountedRef.current) {
                setReTriggerReviewOpen(false);
            }
            await loadAnalytics();
        } catch (error) {
            if (!isMountedRef.current) {
                return;
            }
            console.error(error);
            dispatch(showError(error));
        } finally {
            if (isMountedRef.current) {
                setReTriggerLoading(false);
            }
        }
    }, [analytics, canReTrigger, dispatch, id, loadAnalytics]);

    return (
        <TitledContainer id="campaign-analytics" title={t(TK.analytics)}>
            {loading ? (
                <LoadingState><Spinner /></LoadingState>
            ) : analytics && (
                <PageBody>
                    <CampaignAnalyticsHeader
                        analytics={analytics}
                        onBackToBuilder={backToBuilder}
                        onReTrigger={openReTriggerReview}
                        onRefresh={handleRefresh}
                        canReTrigger={canReTrigger}
                        reTriggerLoading={reTriggerLoading}
                        refreshLoading={refreshLoading}
                    />

                    <CampaignMetricStrip analytics={analytics} />

                    {canReTrigger ? (
                        <ReTriggerBannerEligible>{reTriggerLabel}</ReTriggerBannerEligible>
                    ) : (
                        <ReTriggerBannerCooldown>{reTriggerLabel}</ReTriggerBannerCooldown>
                    )}

                    <TwoColumnGrid>
                        <CampaignProcessTimeline processes={analytics.processes} />
                        <CampaignFailureBreakdown breakdown={analytics.failureReasonBreakdown} />
                    </TwoColumnGrid>

                    <CampaignRecipientStatusTable rows={analytics.recipientStatusRows} channel={analytics.channel} />
                </PageBody>
            )}

            <CampaignReTriggerReviewModal
                isOpen={reTriggerReviewOpen}
                analytics={analytics}
                loading={reTriggerLoading}
                onClose={closeReTriggerReview}
                onConfirm={confirmReTrigger}
            />
        </TitledContainer>
    );
};
